import { cn } from "@/lib/utils";
import SectionEyebrow from "@/components/services/SectionEyebrow";
import { QA_HERO_EYEBROW, QA_OFFERING_EYEBROWS, QA_SHARED_EYEBROWS } from "@/data/qaSectionEyebrows";
import { useState } from "react";
import { lookupSlug } from "@/lib/slugLookup";
import { Navigate, Link } from "@/lib/router-compat";
import { useStableLocation } from "@/hooks/useStableLocation";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import SEOHead from "@/components/SEOHead";
import Navbar from "@/components/Navbar";
import CommercialBrowseSidebar from "@/components/services/CommercialBrowseSidebar";
import ScrollToTop from "@/components/ScrollToTop";
import Footer from "@/components/Footer";
import ContactCTA from "@/components/ContactCTA";
import WebinarRegistrationForm from "@/components/webinar/WebinarRegistrationForm";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { getServiceBySlug } from "@/data/servicesData";
import ServiceHero from "@/components/services/ServiceHero";
import ServiceWhyQbd from "@/components/services/ServiceWhyQbd";
import ServiceRelatedContent from "@/components/services/ServiceRelatedContent";
import GenericOfferingContent from "@/components/services/GenericOfferingContent";
import BreadcrumbJsonLd from "@/components/BreadcrumbJsonLd";
import { heroTakeaway, keyTakeawaySchema } from "@/data/keyTakeaways";
import {
  InspectionStatBar,
  InspectionStickyCta,
  InspectionFinalCta,
  InspectionRelatedPlaceholder,
  scrollToInspectionForm,
} from "@/components/services/InspectionReadinessBlocks";

import { qaOfferingData } from "@/data/qaOfferingData";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useLanguage } from "@/hooks/useLanguage";
import { getQAOfferingMeta, getLocalizedQAOfferingData, getQAOfferingUI } from "@/i18n/qaOfferings";
import { getServiceContent } from "@/i18n/serviceDetail";
import { localizeHref } from "@/lib/localizeHref";
import { getReviewedQABannerCopy } from "@/lib/qaBannerCopy";

import { SITE_URL as SITE, DEFAULT_OG_IMAGE } from "@/config/site";

/* Per-page hero images (also used as social share + overview card images) */
const QA_HERO_BASE = "https://jrtdedcfhvzaomneervp.supabase.co/storage/v1/object/public/service-images/quality-assurance%2Fheroes";
const OUTSOURCED_QA_HERO = `${QA_HERO_BASE}%2Foutsourced-qa-hero.jpg`;
const GXP_AUDITS_HERO = `${QA_HERO_BASE}%2Fgxp-audits-hero.jpg`;
const QP_RP_HERO = `${QA_HERO_BASE}%2Fqp-rp-hero.jpg`;
const APR_PQR_HERO = `${QA_HERO_BASE}%2Fapr-pqr-hero.jpg`;

interface FaqItem { q: string; a: string; }

/* ── Spanish slug → English contentKey mapping ── */
const ES_SLUG_MAP: Record<string, string> = {
  "implementacion-qms": "qms-setup",
  "mantenimiento-qms": "qms-remediation-support",
  "externalizacion-garantia-de-calidad": "outsourced-qa",
  "persona-cualificada-persona-responsable": "qualified-person-qp-responsible-person-rp",
  "revision-anual-productos": "annual-product-review-apr-product-quality-review-pqr",
  "auditoria-calidad": "audits-and-remediation",
};

const getOfferingSlug = (pathname: string): string | null => {
  const match = pathname.match(/\/(?:services|servicios)\/quality-assurance\/([^/]+)$/);
  if (!match) return null;
  const raw = decodeURIComponent(match[1]);
  return lookupSlug(ES_SLUG_MAP, raw);
};

const offeringPages: Record<string, {
  title: string;
  subtitle: string;
  description: string;
  /** Exact meta title override; defaults to "<title> — <service> | QbD Group" */
  metaTitle?: string;
  contentKey: string;
  ogImage?: string;
  heroImage?: string;
  heroImageAlt?: string;
  faqItems?: FaqItem[];
  contentTags?: string[];
  noIndex?: boolean;
}> = {
  "regulatory-inspection-readiness": {
    title: "Regulatory Inspection Readiness",
    subtitle: "Permanently inspection ready",
    description: "Stay in a state of control so an FDA or EMA inspection is a normal business day. Mock audits by former FDA auditors, Form 483 and Warning Letter remediation, and permanent readiness programs across the US and Europe.",
    contentKey: "inspection-readiness",
    contentTags: ["Quality Assurance", "Pharma & Biotech"],
  },
  "qms-development-optimization": {
    title: "QMS Development & Optimization",
    subtitle: "Ensure compliance and efficiency with tailored Quality Management Systems",
    description: "At QbD Group, we provide QA experts experienced in creating smart, effective, and compliant QMS solutions tailored to your needs. Whether your focus is GMP, GCP, GDP, or ISO standards, we ensure your system drives business value and regulatory compliance.",
    contentKey: "qms-setup",
    contentTags: ["Quality Assurance"],
  },
  "qa-support-outsourcing": {
    title: "QA Support & Outsourcing",
    subtitle: "Simplify Quality Management with Tailored Outsourced QA Services",
    metaTitle: "QA Support & Outsourcing | QbD Group",
    description: "At QbD Group, we take over your quality assurance activities with a dedicated team of experts. From supplier management to deviation handling, we provide seamless, cost-effective solutions so you can focus on your core strategy.",
    contentKey: "outsourced-qa",
    heroImage: OUTSOURCED_QA_HERO,
    heroImageAlt: "External QA consultant working alongside a client's quality team at a pharmaceutical site",
    ogImage: OUTSOURCED_QA_HERO,
    contentTags: ["Quality Assurance"],
    faqItems: [
      { q: "Why should we outsource our quality management system?", a: "Outsourcing helps you access specialized expertise, ensuring compliance without the overhead of managing it in-house. It allows you to focus on your core business while experts handle the complexities of quality management." },
      { q: "How can outsourcing improve compliance?", a: "Outsourcing ensures that your quality system is managed by professionals who are up-to-date with ever-changing regulations. It reduces the risk of compliance errors and ensures your processes are always audit-ready." },
      { q: "How do you know that we and by extension our consultants are reliable?", a: "We carefully train and maintain the experience, certifications, and track record of our consultants. You'll have transparency throughout the process, with regular updates and performance reviews." },
      { q: "Can outsourcing help reduce costs?", a: "Yes, outsourcing eliminates the need for long-term hires and overhead costs, offering a more cost-effective solution. It allows you to pay for services as needed, without sacrificing quality." },
      { q: "What types of quality management services can be outsourced?", a: "You can outsource a range of services including audits, training, regulatory submissions, documentation management, batch review, supplier management, and more. This allows you to tailor the outsourcing solution to fit your needs." },
      { q: "How quickly can outsourcing be implemented?", a: "The timeline depends on your needs, but we work efficiently to integrate outsourcing with minimal disruption. Typically, we can have things up and running within a month." },
      { q: "Will we lose control over our quality processes?", a: "Not at all! You'll remain in control, with the flexibility to set expectations and monitor progress. Outsourcing complements your operations, providing expert support without losing oversight." },
    ],
  },
  "qp-rp-prrc-services": {
    title: "QP, RP & PRRC Services",
    subtitle: "Seamlessly meet regulatory requirements with expert QP and RP support",
    metaTitle: "QP, RP & PRRC Services | QbD Group",
    description: "QbD Group provides experienced Qualified Persons and Responsible Persons to help you meet mandatory requirements, maintain compliance, and ensure operational continuity. From batch release to compliance monitoring, our experts support your needs.",
    contentKey: "qualified-person-qp-responsible-person-rp",
    heroImage: QP_RP_HERO,
    heroImageAlt: "Qualified Person reviewing batch documentation before product release",
    ogImage: QP_RP_HERO,
    contentTags: ["Quality Assurance", "Pharma & Biotech"],
    faqItems: [
      { q: "What qualifications should a QP or RP have?", a: "A Qualified Person (QP) or Responsible Person (RP) should hold relevant degrees in life sciences and certifications that meet regulatory standards such as GMP or GDP. They must have extensive experience and knowledge of EU or FDA regulations." },
      { q: "How quickly can a QP or RP be hired through QbD?", a: "Depending on your needs, a qualified QP or RP can typically be available within a few weeks." },
      { q: "Can a QP or RP work remotely?", a: "Yes, many tasks like compliance documentation and oversight can be done remotely. However, certain responsibilities, such as batch release, may require on-site presence." },
      { q: "How do you ensure the QP or RP stays compliant with regulations?", a: "Our QPs and RPs stay updated through industry courses and certifications. Regular feedback and performance reviews ensure high compliance standards." },
      { q: "What if we need a QP or RP for a short-term project?", a: "We offer flexible, short-term engagements for audits, product launches, or regulatory submissions, providing support without a long-term commitment." },
      { q: "Will we lose control over our compliance processes?", a: "Absolutely not. Our QPs and RPs integrate seamlessly into your team, following your protocols and procedures while providing expert guidance. You retain full oversight and decision-making authority." },
    ],
  },
  "qa-consultancy-apr-pqr": {
    title: "QA Consultancy (APR & PQR)",
    subtitle: "Streamline your APR and PQR processes with expert support",
    metaTitle: "QA Consultancy (APR & PQR) | QbD Group",
    description: "QbD Group offers full support for Annual Product Review and Product Quality Review — from data analysis and PQR writing to compliance review. These mandatory reviews ensure product quality, safety, and market readiness.",
    contentKey: "annual-product-review-apr-product-quality-review-pqr",
    heroImage: APR_PQR_HERO,
    heroImageAlt: "Quality professionals analyzing product trend data for an annual product quality review",
    ogImage: APR_PQR_HERO,
    contentTags: ["Quality Assurance", "Pharma & Biotech"],
    faqItems: [
      { q: "What information do you need from us to start the APR?", a: "We'll need access to relevant product data, batch records, quality metrics, and previous review documents. We'll guide you through the data collection process to ensure everything is in place." },
      { q: "Can you handle complex products with multiple components?", a: "Yes, we have experience with complex products and multi-component systems. Our team ensures every component is properly assessed." },
      { q: "How do you manage confidential product information?", a: "We follow strict protocols to handle your product data securely. All information is shared only within our trusted team." },
      { q: "What if we have multiple products that need APRs?", a: "We can handle APRs for multiple products simultaneously, ensuring consistency and streamlining the process." },
      { q: "How do you address issues or deviations found during the APR?", a: "We identify deviations during the review and provide clear recommendations for corrective actions, complete with a detailed action plan." },
      { q: "Can you integrate our existing quality management system into the APR process?", a: "Yes, we align with your existing QMS to ensure the review process integrates seamlessly with your internal procedures." },
      { q: "How will we know the APR is completed on time?", a: "We provide a clear timeline and regular updates, so you always know when to expect the completed review." },
    ],
  },
  "integrated-audit-solutions": {
    title: "Integrated Audit Solutions",
    subtitle: "Ensure compliance and continuous improvement with expert auditing services",
    metaTitle: "Integrated Audit Solutions | QbD Group",
    description: "QbD Group offers certified and experienced auditors to perform internal and supplier audits on your behalf. Our QA experts can also support you in resolving audit findings effectively, ensuring compliance and driving improvement.",
    contentKey: "audits-and-remediation",
    heroImage: GXP_AUDITS_HERO,
    heroImageAlt: "GxP auditor assessing a pharmaceutical production area together with a site representative",
    ogImage: GXP_AUDITS_HERO,
    contentTags: ["Quality Assurance"],
  },
  "qmsr-compliance": {
    title: "QMSR Compliance Services",
    subtitle: "Align your quality system with the FDA's new rule and stay inspection-ready in the US market.",
    description: "The FDA's Quality Management System Regulation (QMSR) replaced 21 CFR Part 820 and now incorporates ISO 13485:2016 by reference, with FDA-specific requirements layered on top.\u00a0\n\n\nQbD Group helps medical device and IVD manufacturers assess their system, close the gaps, and stay inspection-ready — whether already ISO 13485 certified or building toward US market entry for the first time.",
    contentKey: "qmsr-compliance",
    contentTags: ["Quality Assurance", "Medical Devices"],
  },
};

const QAOffering = () => {
  const location = useStableLocation();
  const [formOpen, setFormOpen] = useState(false);
  const lang = useLanguage();

  const slug = getOfferingSlug(location.pathname);
  const page = slug ? offeringPages[slug] : undefined;
  const parentService = getServiceBySlug("quality-assurance");

  if (!page || !parentService) return <Navigate to={lang === "zh-cn" ? "/zh-cn/services/quality-assurance" : lang === "es" ? "/es/servicios/quality-assurance" : "/en/services/quality-assurance"} replace />;

  const ui = getQAOfferingUI(lang);
  const i18nMeta = getQAOfferingMeta(page.contentKey, lang);

  const displayTitle = i18nMeta?.title ?? page.title;
  const displaySubtitle = i18nMeta?.subtitle ?? page.subtitle;
  const displayDescription = i18nMeta?.description ?? page.description;
  const displayFaqItems = i18nMeta?.faqItems ?? page.faqItems;

  // Service-level i18n for form heading etc.
  const serviceI18n = getServiceContent("quality-assurance", lang);

  const heroService = {
    ...parentService,
    title: displayTitle,
    subtitle: displaySubtitle,
    description: displayDescription,
    path: location.pathname,
    heroImage: page.heroImage ?? parentService.heroImage,
  };

  
  const pageOgImage = page.ogImage || DEFAULT_OG_IMAGE;
  const metaTitle = page.metaTitle ?? `${displayTitle} — ${ui.qualityAssurance} | QbD Group`;
  const metaDesc = displayDescription.slice(0, 155);
  const pageUrl = `${SITE}${location.pathname}`;
  const relatedTags = page.contentTags || ["Quality Assurance"];

  const rawData = qaOfferingData[page.contentKey];
  // Unified eyebrow system applies to the English offering pages that have a curated eyebrow set.
  const unified = lang === "en" && !!QA_OFFERING_EYEBROWS[page.contentKey];
  const data = rawData ? getLocalizedQAOfferingData(page.contentKey, rawData, lang) : undefined;

  const formHeading = serviceI18n?.formHeading ?? parentService.formHeading;
  const formSubtitle = serviceI18n?.formSubtitle ?? parentService.formSubtitle;

  // Reviewed English offerings show the updated banner copy and send the banner
  // button to the contact page instead of opening the form dialog.
  const reviewedBannerCopy = getReviewedQABannerCopy(page.contentKey, lang);

  const isInspectionReadiness = page.contentKey === "inspection-readiness";
  const mockAuditLabel = "Request a mock audit";

  const breadcrumbServiceLabel = ui.qualityAssurance;
  const langPrefix = lang === "zh-cn" ? "/zh-cn" : lang === "es" ? "/es" : "/en";
  const servicesPath = localizeHref("/en/services", lang);
  const qualityAssurancePath = localizeHref("/en/services/quality-assurance", lang);

  return (
    <>
      <SEOHead
        title={metaTitle}
        description={metaDesc}
        canonical={pageUrl}
        ogImage={pageOgImage}
        noIndex={page.noIndex}
      >
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            name: displayTitle,
            description: displayDescription,
            provider: { "@type": "Organization", name: "QbD Group", url: SITE },
            url: pageUrl,
            isPartOf: { "@type": "Service", name: parentService.title, url: `${SITE}${parentService.path}` },
            ...keyTakeawaySchema(heroTakeaway(displaySubtitle, displayDescription)),
          })}
        </script>
        {displayFaqItems && displayFaqItems.length > 0 && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: displayFaqItems.map((faq) => ({
                "@type": "Question",
                name: faq.q,
                acceptedAnswer: { "@type": "Answer", text: faq.a },
              })),
            })}
          </script>
        )}
      </SEOHead>

      
      <BreadcrumbJsonLd
        items={[
          { name: lang === "zh-cn" ? "服务" : lang === "es" ? "Servicios" : "Services", path: servicesPath },
          { name: breadcrumbServiceLabel, path: qualityAssurancePath },
          { name: displayTitle },
        ]}
      />

      <ScrollToTop />
      <Navbar />
      <CommercialBrowseSidebar />

      <main className={cn("pb-16 sm:pb-0", unified && "qa-unified-headings")} style={{ '--service-color': parentService.color } as React.CSSProperties}>
        <ServiceHero
          service={heroService}
          heroImageAlt={page.heroImageAlt}
          eyebrow={unified ? QA_HERO_EYEBROW : undefined}
          onCtaClick={isInspectionReadiness ? scrollToInspectionForm : () => setFormOpen(true)}
          parentCrumbs={[
            { label: breadcrumbServiceLabel, to: qualityAssurancePath },
          ]}
          discoverButtonTarget="service-offerings"
          hideDiscoverButton={isInspectionReadiness}
          primaryCtaLabel={isInspectionReadiness ? mockAuditLabel : undefined}
          secondaryCta={isInspectionReadiness ? { label: "Get in touch", href: "#contact" } : undefined}
        />

        {isInspectionReadiness && (
          <>
            <InspectionStatBar />
            <InspectionStickyCta label={mockAuditLabel} />
          </>
        )}

        {data && <GenericOfferingContent data={data} onCtaClick={() => setFormOpen(true)} hidePartnerSection sectionEyebrows={unified ? QA_OFFERING_EYEBROWS[page.contentKey] : undefined} />}

        <ServiceWhyQbd
          service={parentService}
          onCtaClick={() => setFormOpen(true)}
          partnerOverride={data ? { intro: data.partnerIntro, bullets: data.partnerBullets } : undefined}
          eyebrow={unified ? QA_SHARED_EYEBROWS.partner : undefined}
        />

        {data?.relatedServices && (
          <section className="py-16 sm:py-20 bg-secondary/30">
            <div className="container mx-auto px-6 lg:px-10 max-w-5xl">
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="text-2xl sm:text-3xl font-bold text-foreground mb-10"
              >
                {data.relatedServices.title}
              </motion.h2>
              <div className="bg-background/70 backdrop-blur-sm rounded-3xl border border-border/80 p-6 sm:p-8">
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
                  {data.relatedServices.services.map((service, i) => (
                    <motion.div
                      key={service.title}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: i * 0.1 }}
                      className="h-full"
                    >
                      <Link
                        to={localizeHref(`/en${service.url}`, lang)}
                        className="group flex flex-col h-full bg-background rounded-2xl overflow-hidden border border-border/30 shadow-sm hover:shadow-md transition-shadow"
                      >
                        <div className="aspect-[16/10] overflow-hidden">
                          <img
                            src={service.image}
                            alt={service.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            loading="lazy"
                          />
                        </div>
                        <div className="p-6 flex flex-col flex-1">
                          <h3 className="font-semibold text-foreground text-base mb-2">
                            {service.title}
                          </h3>
                          <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                            {service.description}
                          </p>
                          <span
                            className="inline-flex items-center gap-1.5 text-sm font-semibold mt-auto"
                            style={{ color: "var(--service-color)" }}
                          >
                            Learn more <ArrowRight className="w-4 h-4" />
                          </span>
                        </div>
                      </Link>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {displayFaqItems && displayFaqItems.length > 0 && (
          <section className="py-16 sm:py-20 bg-muted/30">
            <div className="container mx-auto px-6 lg:px-10 max-w-5xl">
              {unified ? <SectionEyebrow>{QA_SHARED_EYEBROWS.faq}</SectionEyebrow> : <div className="flex items-center gap-3 mb-2">
                <span className="w-6 h-0.5 rounded-full" style={{ backgroundColor: parentService.color }} />
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{({ en: "Quality systems and audits", es: "Sistemas de calidad y auditor\u00eda", "zh-cn": "质量体系审计实践" }[lang])}</p>
              </div>}
              <h2 className="text-lg sm:text-xl font-semibold text-foreground mb-6">{ui.faqTitle}</h2>
              <Accordion type="single" collapsible className="space-y-2">
                {displayFaqItems.map((faq, i) => (
                  <AccordionItem key={i} value={`faq-${i}`} className="bg-background border border-border/40 rounded-lg px-5">
                    <AccordionTrigger className="text-sm font-medium text-foreground hover:no-underline">
                      {faq.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-muted-foreground leading-relaxed">
                      {faq.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </section>
        )}

        {isInspectionReadiness ? (
          <InspectionRelatedPlaceholder />
        ) : (
          <ServiceRelatedContent service={parentService} contentTags={relatedTags} eyebrow={unified ? QA_SHARED_EYEBROWS.related : undefined} />
        )}

        <Dialog open={formOpen} onOpenChange={setFormOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogTitle className="sr-only">{formHeading}</DialogTitle>
            <WebinarRegistrationForm
              registrationUrl={null}
              title={parentService.title}
              hubspotFormId={parentService.hubspotFormId}
              heading={formHeading}
              subtitle={formSubtitle}
            />
          </DialogContent>
        </Dialog>

        {isInspectionReadiness ? (
          <InspectionFinalCta hubspotFormId={parentService.hubspotFormId} />
        ) : (
          <ContactCTA
            onContactClick={reviewedBannerCopy ? undefined : () => setFormOpen(true)}
            title={formHeading}
            description={reviewedBannerCopy ?? formSubtitle}
          />
        )}
      </main>

      <Footer />
    </>
  );
};

export default QAOffering;
